import { useFormikContext } from "formik";
import { v4 as uuidv4 } from "uuid";

import {
  useDeleteDealOverviewMetric,
  useDeleteDealOverviewSection,
} from "@vega-api/generated/coinvest-deal-overview/coinvest-deal-overview";
import { DealMetricType } from "@vega-api/generated/types/dealMetricType";
import { type DealOverviewSectionRequest } from "@vega-api/generated/types/dealOverviewSectionRequest";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import { type FormMetric } from "@vega-modules/scenes/CoInvestmentsGP/utils/metricTransforms";

import { type CoInvestmentEditFormData } from "../../CoInvestmentEditForm";
import { MODAL_TYPES, type ModalType } from "../constants/modalConstants";

type AddItemParams = {
  modalType: ModalType;
  name: string;
  value: string;
  metricType?: DealMetricType;
};

export const useDealOverviewActions = () => {
  const { values, setFieldValue } =
    useFormikContext<CoInvestmentEditFormData>();
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();

  const createMutationHandler = (successMessage: string) => ({
    onSuccess: () => {
      showToast({
        title: successMessage,
        variant: "success",
      });
    },
    onError: (error: Parameters<typeof showErrorToast>[0]) => {
      showErrorToast(error);
    },
  });

  const deleteMetricMutation = useDeleteDealOverviewMetric({
    mutation: createMutationHandler(
      m.coInvestments_overview_success_dealOverviewMetricRemoved(),
    ),
  });

  const deleteSectionMutation = useDeleteDealOverviewSection({
    mutation: createMutationHandler(
      m.coInvestments_overview_success_dealOverviewSectionRemoved(),
    ),
  });

  const addItem = ({
    modalType,
    name,
    value,
    metricType,
  }: AddItemParams): boolean => {
    if (!values.dealOverview) {
      setFieldValue("dealOverview", { metrics: [], sections: [] });
    }

    if (modalType === MODAL_TYPES.METRIC) {
      const metricValue =
        metricType === DealMetricType.MONEY
          ? { amount: value, currency: values.currency }
          : value;

      const newMetric: FormMetric = {
        id: uuidv4(),
        name,
        value: metricValue,
        type: metricType ?? DealMetricType.TEXT,
        isEditable: true,
      };
      const currentMetrics = values.dealOverview?.metrics || [];
      setFieldValue("dealOverview.metrics", [...currentMetrics, newMetric]);
    } else {
      const newSection: DealOverviewSectionRequest = {
        id: uuidv4(),
        name,
        content: value,
        isEditable: true,
      };
      const currentSections = values.dealOverview?.sections || [];
      setFieldValue("dealOverview.sections", [...currentSections, newSection]);
    }

    return true;
  };

  const removeItem = (type: "metrics" | "sections", name: string) => {
    if (!values.dealOverview || !values.id) return;

    if (type === "metrics") {
      const metric = values.dealOverview.metrics.find((m) => m.name === name);
      const filteredMetrics = values.dealOverview.metrics.filter(
        (m) => m.name !== name,
      );

      if (!metric?.id) {
        setFieldValue("dealOverview.metrics", filteredMetrics);

        return;
      }

      deleteMetricMutation.mutate(
        { dealId: values.id, metricId: metric.id },
        {
          onSuccess: () =>
            setFieldValue("dealOverview.metrics", filteredMetrics),
        },
      );

      return;
    }

    const section = values.dealOverview.sections.find((s) => s.name === name);
    const filteredSections = values.dealOverview.sections.filter(
      (s) => s.name !== name,
    );

    if (!section?.id) {
      setFieldValue("dealOverview.sections", filteredSections);

      return;
    }

    deleteSectionMutation.mutate(
      { dealId: values.id, sectionId: section.id },
      {
        onSuccess: () =>
          setFieldValue("dealOverview.sections", filteredSections),
      },
    );
  };

  const getExistingNames = (
    type: "metrics" | "sections",
  ): { name: string; metricType?: DealMetricType }[] => {
    if (type === "metrics") {
      return (values.dealOverview?.metrics || []).map((m) => ({
        name: m.name,
        metricType: m.type,
      }));
    }

    return (values.dealOverview?.sections || []).map((s) => ({
      name: s.name,
    }));
  };

  const getItems = (type: "metrics" | "sections") => {
    if (type === "metrics") {
      return values.dealOverview?.metrics || [];
    }

    return values.dealOverview?.sections || [];
  };

  return {
    addItem,
    removeItem,
    getExistingNames,
    getItems,
    values,
    isRemovingMetric: deleteMetricMutation.isPending,
    isRemovingSection: deleteSectionMutation.isPending,
  };
};
